import React from 'react';

import { Heading, HeadingColor, HeadingElement as HeadingElementType } from './Headings.styles';

type HeadingElementProps = React.HTMLAttributes<HTMLHeadingElement> & {
  component: HeadingElementType;
  presentation?: HeadingElementType;
  strong?: boolean;
  serif?: boolean;
  color?: HeadingColor;
};

type HeadingProps = Omit<HeadingElementProps, 'component'>;

/**
 * Zola-UI 3.0 compliant header element.
 *
 * - Remove all margin / padding
 * - Applies the correct font size / line height / letter spacing
 * - Picks the right font based on the strong flag (circular or new spirit)
 * - Switches to the appropriate font on mobile
 *
 * Separates presentation from semantics to allow for SEO optimizations.  For example
 * styling an H1 as a h4 may give a nice UI presentation, but tell SEO that the H1
 * content is important.
 *
 * see https://www.figma.com/file/ie3lgUYVHyTtIfkZ5Y6y62/v3_Zola-PD_Style_Library?type=design&node-id=3-49543&t=KmeP1E0dO4fABfaN-4
 */
export const HeadingElement = ({
  component = 'h1',
  presentation,
  className,
  strong,
  serif,
  color = 'BLACK_100',
  ...rest
}: HeadingElementProps): JSX.Element => {
  return (
    <Heading
      as={component}
      presentation={presentation || component}
      strong={strong}
      serif={serif}
      className={className}
      color={color}
      {...rest}
    />
  );
};

/**
 * Zola-UI 3.0 compliant H1 element.
 *
 * @example
 * <Title1>For all the days along the way</Title1>
 *
 * @example
 * <Title1 presentation='h4'>For all the days along the way</Title1>
 */
export const Title1 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h1" {...props} />
);
export const TitleMedium1 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h1" strong {...props} />
);
export const TitleAccent1 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h1" serif {...props} />
);

/**
 * Zola-UI 3.0 compliant H2 element.
 */
export const Title2 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h2" {...props} />
);
export const TitleMedium2 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h2" strong {...props} />
);
export const TitleAccent2 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h2" serif {...props} />
);

/**
 * Zola-UI 3.0 compliant H3 element.
 */
export const Title3 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h3" {...props} />
);
export const TitleMedium3 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h3" strong {...props} />
);
export const TitleAccent3 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h3" serif {...props} />
);

/**
 * Zola-UI 3.0 compliant H4 element.
 */
export const Title4 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h4" {...props} />
);
export const TitleMedium4 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h4" strong {...props} />
);
export const TitleAccent4 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h4" serif {...props} />
);

/**
 * Zola-UI 3.0 compliant H5 element.
 */
export const Title5 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h5" {...props} />
);
export const TitleMedium5 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h5" strong {...props} />
);
export const TitleAccent5 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h5" serif {...props} />
);

/**
 * Zola-UI 3.0 compliant H6 element.
 */
export const Title6 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h6" {...props} />
);
export const TitleMedium6 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h6" strong {...props} />
);
export const TitleAccent6 = (props: HeadingProps): JSX.Element => (
  <HeadingElement component="h6" serif {...props} />
);

export default {
  Title1,
  TitleMedium1,
  TitleAccent1,
  Title2,
  TitleMedium2,
  TitleAccent2,
  Title3,
  TitleMedium3,
  TitleAccent3,
  Title4,
  TitleMedium4,
  TitleAccent4,
  Title5,
  TitleMedium5,
  TitleAccent5,
  Title6,
  TitleMedium6,
  TitleAccent6,
  TitleBase: HeadingElement,
};
